import type { ComponentType } from "react";

import {
  ALL_STEPS,
  FLOW_STEPS,
  TRACK_STEP,
  type FlowStepId,
  type StepId,
} from "./flow";
import {
  EvidenceScreen,
  ItemSelectionScreen,
  LookupScreen,
  ReasonScreen,
  ResolutionScreen,
  ReviewScreen,
  TrackScreen,
} from "./screens";

export { ALL_STEPS, FLOW_STEPS, TRACK_STEP };
export type { FlowStepId, StepId };

/**
 * Routing.
 *
 * Three routes: `/:merchantSlug/returns` and `/:merchantSlug/returns/track` from docs/08,
 * plus `/:merchantSlug/returns/r/:token`, the track link minted with a return, which opens
 * that return's tracking view. Matching is a pure function over a pathname so the whole route
 * table is testable with no DOM and no router library — and `react-router-dom` is not a
 * dependency of this app, so a ~50-line matcher is cheaper than a dependency the portal
 * would use for three patterns.
 */

export type RouteName = "flow" | "track" | "link";

export interface RouteMatch {
  name: RouteName;
  merchantSlug: string;
  /** Present on `link` only. The opaque token, never parsed or trusted by the client. */
  token?: string;
}

/** A slug is a URL segment: lowercase alphanumerics and hyphens. Anything else is not a route. */
const SLUG = /^[a-z0-9][a-z0-9-]*$/i;

/**
 * A link token is opaque to us: URL-safe characters and a length band, nothing more. The
 * shape check exists so a stray path segment does not become a network call — it is NOT
 * validation. Only the server can say whether a token is real, and it deliberately answers
 * the same way whether the token is expired, revoked or never existed.
 */
const LINK_TOKEN = /^[A-Za-z0-9_-]{8,128}$/;

export function matchRoute(pathname: string): RouteMatch | null {
  const segments = pathname
    .split("/")
    .filter((s) => s.length > 0)
    .map(decodeURIComponent);

  const [slug, returns, tail, token, ...rest] = segments;
  if (slug === undefined || !SLUG.test(slug)) return null;
  if (returns !== "returns") return null;
  if (rest.length > 0) return null;

  if (tail === "r") {
    if (token === undefined || !LINK_TOKEN.test(token)) return null;
    return { name: "link", merchantSlug: slug, token };
  }
  // Every other route is exactly two or three segments long.
  if (token !== undefined) return null;
  if (tail === undefined) return { name: "flow", merchantSlug: slug };
  if (tail === "track") return { name: "track", merchantSlug: slug };
  return null;
}

export function buildPath(
  name: RouteName,
  merchantSlug: string,
  token?: string,
): string {
  const base = `/${encodeURIComponent(merchantSlug)}/returns`;
  if (name === "track") return `${base}/track`;
  if (name === "link") {
    if (token === undefined) {
      throw new Error('buildPath("link") needs a token');
    }
    return `${base}/r/${encodeURIComponent(token)}`;
  }
  return base;
}

/**
 * Every step maps to exactly one component. Adding a screen body means editing the screen
 * file only — this table, the router and the providers stay untouched.
 */
export const SCREENS: Record<StepId, ComponentType> = {
  lookup: LookupScreen,
  items: ItemSelectionScreen,
  reason: ReasonScreen,
  evidence: EvidenceScreen,
  resolution: ResolutionScreen,
  review: ReviewScreen,
  track: TrackScreen,
};

/**
 * Which step a matched route starts on. A `link` route starts on lookup too: the deep-link
 * screen stands in front of it and shows tracking once the token resolves, so a dead link
 * degrades to the ordinary lookup with no special step to unwind.
 */
export function initialStepFor(match: RouteMatch): StepId {
  return match.name === "track" ? TRACK_STEP : "lookup";
}

export function screenFor(step: StepId): ComponentType {
  return SCREENS[step];
}

/**
 * Client-side navigation without a router library. History only; no full reload.
 *
 * `state` rides on the history entry, which is how the review step hands a just-submitted
 * return to the tracking route without browser storage: it lives with that one entry,
 * survives a reload of it, and is gone the moment the shopper navigates away.
 */
export function navigate(path: string, state: unknown = {}): void {
  if (typeof window === "undefined") return;
  window.history.pushState(state, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
